import { useCallback } from 'react';
import * as Y from 'yjs';
import { canvasRoots } from '../wiring/canvas-roots';
import { useCanvasBinding } from '../wiring/contexts/canvas-binding-context';

/**
 * Delete a frame and every direct child it contains. Mirrors the user's
 * mental model that a frame is the unit of grouping — when the group
 * goes, the contents go with it. Single transact ⇒ single undo step,
 * so Cmd+Z restores the frame and all children together.
 *
 * Concurrent edits: another client pushing into this frame's
 * childOrder, or attaching a new child to this frame, after the
 * delete-transact will see those writes dropped on merge (LWW —
 * delete on the parent wins). Any child whose parentId still
 * references this deleted frame after merge is an orphan;
 * useNodesFromYjs filters them out at render time.
 */
export function useFrameDelete(yDoc: Y.Doc, origin: number) {
  const binding = useCanvasBinding();
  const deleteFrame = useCallback((frameId: string) => {
    const { nodes, zOrder } = canvasRoots(yDoc, binding);
    const frame = nodes.get(frameId) as Y.Map<unknown> | undefined;
    if (!frame) return;

    // Locked frames cannot be deleted — same guard as move/resize.
    if (frame.get('locked') === true) return;

    const data = frame.get('data') as Y.Map<unknown> | undefined;
    const childOrder = data?.get('childOrder') as Y.Array<string> | undefined;
    const childIdsFromOrder = childOrder?.toArray() ?? [];

    // Defensive: also pick up any node whose parentId points at this
    // frame but that's missing from childOrder (concurrent attach race
    // where the childOrder push lost). Walking the nodes map once
    // covers both paths.
    const childIds = new Set<string>(childIdsFromOrder);
    nodes.forEach((node) => {
      if (!(node instanceof Y.Map)) return;
      if (node.get('parentId') === frameId) {
        const id = node.get('id');
        if (typeof id === 'string') childIds.add(id);
      }
    });

    yDoc.transact(() => {
      // Collect zOrder indices once and delete from the highest down so
      // earlier deletions don't shift later target indices.
      const arr = zOrder.toArray();
      const indicesToDelete: number[] = [];
      for (const childId of childIds) {
        const idx = arr.indexOf(childId);
        if (idx !== -1) indicesToDelete.push(idx);
        nodes.delete(childId);
      }
      const frameIdx = arr.indexOf(frameId);
      if (frameIdx !== -1) indicesToDelete.push(frameIdx);
      indicesToDelete.sort((a, b) => b - a);
      for (const idx of indicesToDelete) zOrder.delete(idx, 1);

      // Drop the frame Y.Map last. Deleting the frame Y.Map cascades GC
      // to its `data` and `childOrder` types.
      nodes.delete(frameId);
    }, origin);
  }, [yDoc, origin, binding]);

  return { deleteFrame };
}
